import React from 'react';
import { Chip, ChipTab } from '../../lib/types';
import PortalTable, { Column } from '../../components/PortalTable/PortalTable';
import ChipRow from './ChipRow';
import styles from './Chips.module.scss';

const stockColumns: Column[] = [
  { key: 'chipId', label: 'Chip ID' },
  { key: 'petName', label: 'Pet Name' },
  { key: 'breed', label: 'Breed' },
  { key: 'dateAdded', label: 'Date Added' },
  { key: 'status', label: 'Status' },
  { key: 'action', label: 'Action' },
];

const registeredColumns: Column[] = [
  { key: 'chipId', label: 'Chip ID' },
  { key: 'petName', label: 'Pet Name' },
  { key: 'breed', label: 'Breed' },
  { key: 'owner', label: 'Owner' },
  { key: 'registered', label: 'Registered' },
  { key: 'registeredBy', label: 'Registered By' },
  { key: 'action', label: 'Action' },
];

const searchColumns: Column[] = [
  { key: 'chipId', label: 'Chip ID' },
  { key: 'petName', label: 'Pet Name' },
  { key: 'breed', label: 'Breed' },
  { key: 'owner', label: 'Owner' },
  { key: 'date', label: 'Date' },
  { key: 'registeredBy', label: 'Registered By' },
  { key: 'status', label: 'Status' },
  { key: 'action', label: 'Action' },
];

interface ChipsTableProps {
  chips: Chip[];
  tab: ChipTab;
  searchQuery: string;
  /** When true, the own-stock search query is in flight — defer the empty-state
   * lookup slot so we don't fire the external lookup (or briefly flash the
   * "External" tab) before own-stock results have arrived. */
  searchLoading?: boolean;
  onRegister: (chipId: string) => void;
  onDetail: (chip: Chip) => void;
  onTransfer: (chip: Chip) => void;
  onRecall: (chip: Chip) => void;
  externalLookupSlot?: React.ReactNode;
}

export default function ChipsTable({
  chips,
  tab,
  searchQuery,
  searchLoading,
  onRegister,
  onDetail,
  onTransfer,
  onRecall,
  externalLookupSlot,
}: ChipsTableProps) {
  const columns = tab === 'all' ? searchColumns : tab === 'stock' ? stockColumns : registeredColumns;

  const isSearching = tab === 'all';
  const showSearchEmpty = isSearching && chips.length === 0;
  const showDefaultEmpty = !isSearching && chips.length === 0;

  return (
    <PortalTable columns={columns} isEmpty={showDefaultEmpty} emptyMessage="No chips in this category yet">
      {showSearchEmpty ? (
        <tr className={styles.searchEmptyRow}>
          <td colSpan={columns.length} className={styles.searchEmptyCell}>
            {searchLoading
              ? null
              : (externalLookupSlot ?? (
                  <h3 className={styles.lookupHeading}>No match found in your stock for &ldquo;{searchQuery}&rdquo;</h3>
                ))}
          </td>
        </tr>
      ) : (
        chips.map((chip) => (
          <ChipRow
            key={chip.id}
            chip={chip}
            tab={tab}
            onRegister={onRegister}
            onDetail={onDetail}
            onTransfer={onTransfer}
            onRecall={onRecall}
          />
        ))
      )}
    </PortalTable>
  );
}
